import React, {Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState} from 'react';

import {SelectInterface} from '@veneer/core';

import {Product, ProductSku} from '@br/idm-api';

import SelectMultipleFilter from './SelectMultipleFilter';

const ProductFilter = <K extends string, F extends Partial<Record<K, string[]>>>({
    field,
    label,
    filters,
    setFilters,
    getProductSkus,
}: {
    label: string;
    field: K;
    filters: F;
    setFilters: Dispatch<SetStateAction<F>>;
    getProductSkus: () => Promise<ProductSku[]>;
}): JSX.Element => {
    const [{productSkus, loading}, setState] = useState<{
        loading: boolean;
        productSkus: ProductSku[];
    }>({
        loading: true,
        productSkus: [],
    });
    const [productFilter, setProductFilter] = useState('');

    // Here, we extract the Product objects from the ProductSku objects, provided by productApi.
    // This is because the Product model doesn't have its own API for this purpose.
    const products: Product[] = useMemo(
        () => [
            ...new Map(
                productSkus.flatMap(product =>
                    product.edition?.product ? [[product.edition.product.id, product.edition.product]] : []
                )
            ).values(),
        ],
        [productSkus]
    );

    const filteredProducts = useMemo(() => {
        const filter = productFilter.toLocaleLowerCase();
        return products
            .filter(product => product.name.toLocaleLowerCase().includes(filter))
            .map(product => ({value: product.id, label: product.name}));
    }, [products, productFilter]);

    useEffect(() => {
        void getProductSkus().then(allProducts => setState({productSkus: allProducts, loading: false}));
    }, [getProductSkus]);

    const handleSearch = useCallback<Required<SelectInterface>['onSearch']>(query => setProductFilter(query || ''), []);

    return (
        <SelectMultipleFilter
            data-testid="product-filter"
            label={label}
            field={field}
            showSearch
            filters={filters}
            setFilters={setFilters}
            options={filteredProducts}
            onSearch={handleSearch}
            disabled={loading}
        />
    );
};

export default ProductFilter;
